import { ComponentProps, ReactElement, ReactNode } from 'react';
import { Dropdown as BSDropdown, OverlayTrigger } from 'react-bootstrap';
import { IconType } from 'react-icons';
import { MdCheckBox, MdCheckBoxOutlineBlank, MdIndeterminateCheckBox } from 'react-icons/md';
import {
  Appearance,
  AppearanceType,
  CheckState,
  CheckStateType,
  SizeType,
  StretchModeType
} from '../designConstants';

export const DropdownToggleType = {
  TEXT_INPUT: 'text-input',
  WIDGET_TITLE: 'widget-title',
  PAGE_TITLE: 'page-title',
  ICON_ONLY: 'icon-only'
} as const;
export type DropdownToggleTypeValue = (typeof DropdownToggleType)[keyof typeof DropdownToggleType];

export interface MenuItem<T> {
  className?: string;
  itemIndex?: number;
  itemOptions?: ReactElement; // If the page opens a submenu, pass it in here
  label?: string;
  link?: string;
  menuIndex?: number;
  selected?: boolean;
  value: T;
  section?: string;
  leadingContent?: ReactElement;
  trailingContent?: ReactElement;
  simpleTooltip?: string;
  simpleTooltipVerticalShift?: string;
  disabled?: boolean;
  // One tier only: a child's own `children` are ignored.
  children?: MenuItem<T>[];
  // Set by the dropdown on child rows; callers don't pass it.
  parentValue?: T;
}

// Keys are the selected parents; each array holds that parent's selected children. A parent with
// an empty array is selected on its own.
export type SubmenuSelection<T extends string> = Partial<Record<T, T[]>>;

export interface SubmenuControlProps<T extends string> {
  submenuValue: SubmenuSelection<T>;
  onSubmenuChange: (selection: SubmenuSelection<T>) => void;
}

export type MultiMenuSections = Record<
  string,
  { label: string; displayLabel: boolean; footer?: ReactElement | null }[]
>;

// A single-select pill in the DropdownMenu pill row. A pill with no `filter` is the
// "All" pill (shows everything). When provided, the active pill's `filter` is ANDed
// with the text-search filter to narrow the visible menu items.
export interface DropdownFilterPill<T> {
  label: string;
  filter?: (item: MenuItem<T>) => boolean;
}

export type DropdownOption<T extends string = string> = T | MenuItem<T>;

export const typeAppearance: Record<DropdownToggleTypeValue, AppearanceType> = {
  [DropdownToggleType.TEXT_INPUT]: Appearance.OUTLINED,
  [DropdownToggleType.WIDGET_TITLE]: Appearance.EMPTY,
  [DropdownToggleType.PAGE_TITLE]: Appearance.EMPTY,
  [DropdownToggleType.ICON_ONLY]: Appearance.EMPTY
};

export const CHECK_STATE_ICONS: Record<CheckStateType, IconType> = {
  [CheckState.UNCHECKED]: MdCheckBoxOutlineBlank,
  [CheckState.CHECKED]: MdCheckBox,
  [CheckState.INDETERMINATE]: MdIndeterminateCheckBox
};

export const DropdownVariant = {
  DEFAULT: 'default',
  CELL: 'cell'
} as const;
export type DropdownVariantType = (typeof DropdownVariant)[keyof typeof DropdownVariant];

export interface DropdownSharedProps<T extends string> {
  type?: DropdownToggleTypeValue;
  label?: string | ReactElement;
  /** Accessible name for the toggle when there's no visible `label`. */
  ariaLabel?: string;
  options?: readonly DropdownOption<T>[];
  leadingIcon?: ReactElement;
  size?: SizeType;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  overlay?: ComponentProps<typeof OverlayTrigger>['overlay'];
  menuAlign?: ComponentProps<typeof BSDropdown>['align'];
  hideCaret?: boolean;
  clearable?: boolean;
  onClear?: () => void;
  formatOptionLabel?: (
    option: { label: string; value: string },
    meta?: { context: 'menu' | 'value' }
  ) => ReactNode;
  menuPortalTarget?: HTMLElement | null;
  stretch?: StretchModeType;
  shouldTruncateOptions?: boolean;
  hasError?: boolean;
  onMenuClose?: () => void;
}

// Non-generic alias for consumers (e.g. EditableDropdown) that don't need to
// parameterize `T` — keeps DropdownSharedProps itself an internal detail of the
// Dropdown family instead of pushing the generic across every extend site.
export type DropdownBaseProps = DropdownSharedProps<string>;
